<template>
  <v-dialog v-model="visible" :persistent="running" max-width="1000px">
    <v-card>
      <v-card-title>Sync selected operations to Odoo</v-card-title>
      <v-card-text>
        <p>Creates and confirms one Odoo sales order per operation.</p>
        <p class="mt-2">{{ rows.length }} selected · {{ readyCount }} ready · {{ syncedCount }} synced · {{ skippedCount }} skipped · {{ failedCount }} need attention</p>
        <v-progress-linear v-if="running" class="mt-3" :model-value="progress" color="primary" />
        <v-alert v-if="notice" class="mt-3" type="warning" variant="tonal">{{ notice }}</v-alert>
        <v-table class="mt-3" density="compact">
          <thead><tr><th>Ticket</th><th>Customer</th><th>Sale</th><th>Currency</th><th>Status</th><th>Details</th></tr></thead>
          <tbody>
            <tr v-for="row in rows" :key="row.id">
              <td>{{ row.ticket || row.id }}</td><td>{{ row.customer }}</td>
              <td class="text-right">{{ row.amount }}</td><td>{{ row.currency }}</td>
              <td class="text-no-wrap">{{ row.status }}</td><td>{{ row.message }}</td>
            </tr>
          </tbody>
        </v-table>
      </v-card-text>
      <v-card-actions>
        <v-btn v-if="running" :disabled="stopRequested" @click="stopRequested = true">Stop after current</v-btn>
        <v-spacer />
        <v-btn :disabled="running" @click="visible = false">Close</v-btn>
        <v-btn color="primary" :disabled="!canStart" :loading="running" @click="start">Start sync</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios';
import { getApiErrorMessage } from '../utils/api';

function skipReason(operation) {
  if (!operation) return 'Operation is no longer in the current list; refresh before syncing.';
  if (operation.odoo_so) return `Already linked to Odoo SO ${operation.odoo_so}.`;
  if (!['flight', 'hotel'].includes(operation.type)) return 'Only flight and hotel operations are supported.';
  if (!Number.isInteger(operation.__v) || operation.__v < 0) return 'Operation version is missing; refresh before syncing.';
  return '';
}

export default {
  props: {
    canSync: Boolean,
    busy: Boolean,
    selectedIds: { type: Array, default: () => [] },
    operations: { type: Array, default: () => [] },
  },
  emits: ['synced', 'syncing'],
  data() {
    return { visible: false, running: false, hasRun: false, stopRequested: false, rows: [], notice: '' };
  },
  beforeUnmount() {
    this.stopRequested = true;
  },
  computed: {
    readyCount() { return this.rows.filter(row => row.status === 'Ready').length; },
    syncedCount() { return this.rows.filter(row => row.status === 'Synced').length; },
    skippedCount() { return this.rows.filter(row => row.status === 'Skipped').length; },
    failedCount() { return this.rows.filter(row => ['Failed', 'Review needed'].includes(row.status)).length; },
    progress() { return this.rows.length ? this.rows.filter(row => !['Ready', 'Syncing'].includes(row.status)).length / this.rows.length * 100 : 0; },
    canStart() { return this.canSync && !this.busy && !this.running && !this.hasRun && this.readyCount > 0; },
  },
  methods: {
    open(ids = this.selectedIds) {
      if (!this.canSync || this.busy || this.running || !ids.length) return;
      this.rows = [...new Set(ids)].map(id => {
        const operation = this.operations.find(item => item._id === id);
        const reason = skipReason(operation);
        return { id, ticket: operation?.ticket_number || '', customer: operation?.customer || '',
          amount: operation?.sale_price ?? '', currency: operation?.currency || '',
          status: reason ? 'Skipped' : 'Ready', message: reason };
      });
      this.hasRun = false; this.stopRequested = false; this.notice = ''; this.visible = true;
    },
    async start() {
      if (!this.canStart) return;
      this.running = true; this.hasRun = true;
      this.$emit('syncing', true);
      try {
        for (const row of this.rows) {
          if (row.status !== 'Ready') continue;
          if (this.stopRequested || !this.canSync) {
            this.notice = this.canSync ? 'Stopped after the current operation. Remaining operations were not synced.' : 'Sync permission is no longer available.';
            break;
          }
          // Read the latest local version just before each request; do not send
          // the versions captured when the dialog was opened.
          const operation = this.operations.find(item => item._id === row.id);
          const reason = skipReason(operation);
          if (reason) { row.status = 'Skipped'; row.message = reason; continue; }
          row.status = 'Syncing'; row.message = '';
          try {
            const { data } = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/operation/odoo/so/${operation._id}/v/${operation.__v}`);
            if (!data || data._id !== row.id || !data.odoo_so) {
              row.status = 'Review needed'; row.message = 'Unexpected response. Check Odoo and refresh this operation before retrying.';
              this.notice = 'Stopped because the last operation could not be verified. No automatic retry was made.';
              break;
            }
            row.status = 'Synced'; row.message = `Odoo SO ID ${data.odoo_so}`;
            this.$emit('synced', data);
          } catch (error) {
            const status = error.response?.status;
            const uncertain = !status || status >= 500 || status === 408;
            row.status = uncertain ? 'Review needed' : 'Failed';
            row.message = getApiErrorMessage(error, error.message || 'Odoo sync failed.');
            if (uncertain) row.message += ' Check Odoo and refresh this operation before retrying.';
            if (uncertain || [401, 403, 429].includes(status)) {
              this.notice = uncertain
                ? 'Stopped after an uncertain result. Check Odoo before retrying; an SO may already exist.'
                : 'Stopped because access was denied or another sync request is being processed. Refresh before starting another batch.';
              break;
            }
          }
        }
      } finally {
        for (const row of this.rows) if (row.status === 'Ready') {
          row.status = 'Not run'; row.message = 'No sync request was sent.';
        }
        this.running = false;
        this.$emit('syncing', false);
      }
    },
  },
};
</script>
